import { Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import Alert from 'app/components/Alert/Alert';
import { displayGlobalMessage } from 'app/containers/Act/actions';
import AlertSeverity from 'app/ui/shared/severity/AlertSeverity';
import Button from 'application/components/button';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { firestore } from 'server/Firebase';
import { ActivityPlace, ActivityType } from 'shared/medyx-core/types/activity';
import { LumpSum } from 'shared/medyx-core/types/lump-sum';
import { Mixte } from 'shared/medyx-core/types/mixte';

interface Props {
  placesPreferences: ActivityPlace[];
  period: Mixte | LumpSum;
}

const getRelevantDaysOffForPeriod = (
  place: ActivityPlace | undefined,
  periodStartDate: number | undefined,
  periodEndDate: number | undefined
) => {
  if (!place) return [];
  if (!place.daysOff) return [];

  return place.daysOff.filter((day) => {
    if (!periodStartDate || !periodEndDate) return false;

    const dayDate = new Date(day);
    const dayValue = dayDate.valueOf();
    return dayValue >= periodStartDate && dayValue <= periodEndDate;
  });
};

const PlaceUpdater = ({ placesPreferences, period }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { userId } = useParams();
  const periodStartDate = period.startDate?.valueOf();
  const periodEndDate = period.endDate?.valueOf();

  const isAdminConsole = !!userId;
  if (!isAdminConsole) return null;

  const placePreference = placesPreferences.find((place) => place.number === period.place.number);
  const placePreferenceSignatory = placePreference?.signatory;

  const isSameSignatory =
    placePreferenceSignatory?.firstName === period.place.signatory?.firstName &&
    placePreferenceSignatory?.lastName === period.place.signatory?.lastName &&
    placePreferenceSignatory?.email === period.place.signatory?.email;

  const periodDaysOff = getRelevantDaysOffForPeriod(period.place, periodStartDate, periodEndDate);
  const placePreferenceDaysOff = getRelevantDaysOffForPeriod(placePreference, periodStartDate, periodEndDate);

  const isSameDaysOff =
    placePreferenceDaysOff.every((day) => periodDaysOff.includes(day)) &&
    periodDaysOff.every((day) => placePreferenceDaysOff.includes(day));

  if (isSameSignatory && isSameDaysOff) return null;

  const periodType = period.type === ActivityType.MIXTE ? 'Mixte' : 'Forfaitaire';

  return (
    <Alert
      severity={AlertSeverity.RECOMMENDATION}
      noMargin
      showIcon
      title="Attention"
      message="Les informations de la période ne correspondent pas aux préférences de lieu"
    >
      <Table>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>{periodType}</TableCell>
            <TableCell>Établissement</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>Signataire</TableCell>
            <TableCell>
              {period.place.signatory?.firstName} {period.place.signatory?.lastName} ({period.place.signatory?.email})
            </TableCell>
            <TableCell>
              {placePreferenceSignatory?.firstName} {placePreferenceSignatory?.lastName} (
              {placePreferenceSignatory?.email})
            </TableCell>
          </TableRow>
          {(periodDaysOff.length > 0 || placePreferenceDaysOff.length > 0) && (
            <TableRow>
              <TableCell>Journées fériées</TableCell>
              <TableCell>{periodDaysOff.join(', ') || 'Aucune journée fériée'}</TableCell>
              <TableCell>{placePreferenceDaysOff.join(', ') || 'Aucune journée fériée'}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          theme="primary"
          onClick={async () => {
            if (!period.id) return;

            await firestore.collection('activities').doc(period.id).update({
              place: placePreference
            });

            dispatch(displayGlobalMessage(`${periodType} mis à jour`, 'success'));

            const previousPath = location.pathname.split('/').slice(0, -1).join('/');
            navigate({ pathname: previousPath, search: location.search });
          }}
        >
          Mettre à jour le {periodType}
        </Button>
      </div>
    </Alert>
  );
};

export default PlaceUpdater;
